<template>
  <div>
    <div class="winners mb-4">
      <div class="winners__table-header grid column-gap">
        <b>Submission</b>
        <b>Distribution</b>
        <b class="text-right">MTX</b>
      </div>
      <ul class="list-plain">
        <li
          v-for="submission in localSubs"
          :key="submission.hash"
          class="winners__table-row grid column-gap align-center"
        >
          <div>
            {{ submission.title }}
            <small>by {{ submission.owner | ethAddress }}</small>
          </div>
          <MtxInput
            v-model="submission.distribution"
            @blur="submission.distribution = submission.distribution || 1"
            :label="`Distribution for ${submission.title}`"
            hideLabel
            min="1"
            type="number"
            step="any"
          />
          <span class="text-right">{{ calculateMtx(submission.distribution) }}</span>
        </li>
      </ul>
      <p v-show="$v.localSubs.$error" class="color-red column-span-3 mt-1">
        All submission distributions must be a positive integer.
      </p>
    </div>

    <!-- ACTIONS -->
    <h6>Select winners and:</h6>
    <MtxInput
      v-model="selectedAction"
      type="radio"
      :options="options"
      name="actions"
      stacked
      class="mb-3"
    />

    <!-- NEXT ROUND -->
    <div v-show="selectedAction === 'startNextRound'">
      <h6>Next round details:</h6>

      <MtxInput
        v-model="nextRound.bounty"
        :validation="$v.nextRound.bounty"
        :min="$v.nextRound.bounty.$params.between.min"
        :max="$v.nextRound.bounty.$params.between.max"
        label="Round Bounty"
        description="Cannot exceed the remaining tournament bounty."
        type="number"
        step="any"
        class="mb-2"
        required
      />

      <RoundDatePicker
        ref="roundDatePicker"
        :start="nextRound.start"
        :duration="nextRound.duration"
        @dateChange="Object.assign(nextRound, $event)"
        @invalid="dateInvalid = $event"
      />
    </div>

    <!-- CONFIRMATION -->
    <p>
      <small v-if="selectedAction === 'doNothing'">
        Winners will be selected, but the review period will remain open. If any funds remain on the
        tournament at the end of the review period, a new round will begin automatically. Otherwise,
        the tournament will end.
        <!-- TODO: tournament won't actually end, explain unfunded round -->
      </small>

      <small v-else-if="selectedAction === 'startNextRound'">
        The next round will begin
        <strong>immediately,</strong>
        and end
        <strong>
          <time :datetime="roundEnd">
            {{ roundEnd | date("MMM Do, YYYY") }}
          </time>
        </strong>
        with a reward of
        <strong>{{ nextRound.bounty | mtx }}.</strong>
      </small>
      <small v-else-if="selectedAction === 'closeTournament'">
        The tournament will end
        <strong>now.</strong>
        If there are any remaining funds, they will be distributed to the winners of this round.
      </small>
    </p>

    <div v-if="selectedAction" class="text-center">
      <button @click="confirmWinners" :disabled="isLoading || formInvalid">
        Confirm Winners
      </button>
    </div>
  </div>
</template>

<script>
import { required, between, minValue, integer, decimal } from "@/utils/validators"
import RoundDatePicker from "@/components/tournaments/RoundDatePicker"

const actions = ["doNothing", "startNextRound", "closeTournament"]

export default {
  components: {
    RoundDatePicker
  },

  props: {
    submissions: {
      type: Array,
      default: () => []
    },
    round: {
      type: Object,
      default: () => ({})
    },
    mtxTournament: Object
  },

  data: () => ({
    localSubs: [],
    selectedAction: "",
    options: [
      { value: "doNothing", label: "That's it for now" },
      { value: "closeTournament", label: "End tournament" }
    ],
    remainingMtx: 0,
    nextRound: {
      bounty: 0,
      start: null,
      duration: null,
      review: 7 * 24 * 60 * 60 // 1 week in seconds; TODO: change this?
    },
    dateInvalid: true,
    isLoading: false
  }),

  computed: {
    roundEnd() {
      return new Date(+this.nextRound.start + this.nextRound.duration)
    },

    formInvalid() {
      if (!this.selectedAction) return true

      if (this.selectedAction !== "startNextRound") {
        return this.$v.localSubs.$invalid
      }

      return this.$v.$invalid
    }
  },

  async created() {
    const { bounty, startDate, endDate } = this.round

    this.localSubs = this.submissions.map(submission => ({
      ...submission,
      distribution: 1
    }))

    this.nextRound.bounty = bounty
    this.nextRound.start = new Date()
    this.nextRound.duration = new Date(endDate) - new Date(startDate)

    const tournamentBalance = await this.mtxTournament.getBalance()
    this.remainingMtx = tournamentBalance - bounty

    if (this.remainingMtx > 0) {
      const nextOpt = { value: "startNextRound", label: "Start next round" }
      this.options.push(nextOpt)
    }
  },

  methods: {
    calculateMtx(distribution) {
      if (!distribution) return "*"

      const totalDistributions = this.localSubs.reduce((acc, next) => {
        const val = next.distribution || 0
        return val + acc
      }, 0)
      const res = (+distribution / totalDistributions) * this.round.bounty
      return Math.round(res * 100) / 100
    },

    async confirmWinners() {
      this.$v.$touch()
      this.$refs.roundDatePicker.$v.$touch()

      if (this.isLoading || this.formInvalid) return
      const hashes = this.submissions.map(sub => sub.hash)

      const winnersData = {
        submissions: hashes,
        distribution: hashes.map(() => 1),
        // Must be an index of 0: doNothing, 1: startNextRound, 2: closeTournament
        action: actions.indexOf(this.selectedAction)
      }

      const roundData = {
        duration: Math.floor(this.nextRound.duration / 1000),
        review: this.nextRound.review,
        bounty: this.nextRound.bounty
      }

      this.isLoading = true
      const tx = this.addPlannedTx({
        txFn: () => this.mtxTournament.selectWinners(winnersData, roundData),
        header: "Winner Selection"
      })

      try {
        await tx
        this.$emit("confirmed")
      } finally {
        this.isLoading = false
      }
    }
  },

  validations() {
    return {
      localSubs: {
        $each: {
          distribution: {
            required,
            min: minValue(1),
            integer
          }
        }
      },
      nextRound: {
        bounty: {
          required,
          between: between(1e-18, this.remainingMtx),
          decimal
        },
        validDates: () => !this.dateInvalid
      }
    }
  }
}
</script>

<style lang="scss">
.winners {
  &__table-header,
  &__table-row {
    grid-template-columns: 1fr rem(100px) rem(100px);
    grid-row-gap: 1rem;
  }
}
</style>
